import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Gender, PendingChargeStatus } from '@pflegenavi/shared/api';
import { Filter } from '../filter';
import type { ReceiptTypeName } from '../transaction-phoenix/api';

export type PayoutType =
  | 'earnings'
  | 'direct_spend'
  | 'pliant_spend'
  | 'direct_and_pliant_spend';

export interface PayoutTypeAmount {
  count: number;
  amount: number;
}

export interface AvailableFundsDetails {
  by_payout_type: Partial<Record<PayoutType, PayoutTypeAmount>>;
  total: number;
}

export interface WalletBalanceDetails {
  amount_pending_payout: number;
  amount_blocked_from_payout: number;
  total: number;
  by_payout_type: Partial<Record<PayoutType, PayoutTypeAmount>>;
}

export interface FinanceResult {
  data: {
    nursing_home_id: string;
    payout_mode: 'separate' | 'unified';
    total_resident_balance: number;
    mangopay: {
      wallet: number;
      payins: number;
      payouts: number;
      available_funds_amount: number;
    };
    wallet_balance_details: WalletBalanceDetails;
    available_funds_details: AvailableFundsDetails;
    bank: {
      total: number;
    };
    cash: {
      total: number;
    };
    receipts: {
      draft: number;
    };
  };
}

export interface GetPayoutsResult {
  data: Array<{
    id: string;
    inserted_at: Date;
    transferred: Date | undefined;
    statement_descriptor: string;
    iban_last_4: string;
    amount: number;
    currency: string;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED';
    payout_type: PayoutType;
  }>;
  meta: {
    total_count: number;
    total_pages: number;
  };
}

export interface GetPayoutsParams {
  params: {
    nursing_home_id: string;
    page: number;
    pageSize: number;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED' | undefined;
    orderBy?: 'transferred';
    orderDirection?: 'asc' | 'desc';
  };
}

export interface PayoutReconciliationEntry {
  id: string;
  mangopay_resource_id: string;
  mangopay_transaction_type: 'TRANSFER' | 'PAYOUT';
  mangopay_transaction_nature: 'REGULAR' | 'REFUND';
  amount: number;
  currency: string;
  performed_at: string;
  // Null for the payout itself and for payout refunds.
  receipt: {
    id: string;
    receipt_date: string;
    amount: number;
    resident: { firstname: string; lastname: string };
    service_provider_name: string | null;
    receipt_type_name: ReceiptTypeName | null;
  } | null;
}

export interface GetPayoutReconciliationResult {
  data: PayoutReconciliationEntry[];
}

export interface GetPayoutReconciliationParams {
  params: {
    nursing_home_id: string;
    payout_id: string;
  };
}

export interface GetPayinsResult {
  data: Array<{
    id: string;
    amount: number;
    action_timestamp: Date;
    updated_at: Date;
    charge_date: Date;
    pending_charge_status: Omit<
      PendingChargeStatus,
      | PendingChargeStatus.initializing
      | PendingChargeStatus.accountMissing
      | PendingChargeStatus.dataMissing
    >;
    employee: {
      id: string;
      first_name: string;
      last_name: string;
    };
    resident: {
      id: string;
      firstname: string;
      lastname: string;
      gender: Gender;
    };
  }>;
  meta: {
    total_count: number;
    total_pages: number;
  };
}

export type PayinStatusFilter =
  | 'Success'
  | 'Failed'
  | 'Frozen'
  | 'Upcoming'
  | 'Pending';

export type PayinSortColumn = 'charge_date' | 'updated_at';

export interface PayinFilters {
  /** ISO datetime, inclusive; matched against the action timestamp. */
  actionTimestampFrom?: string;
  /** Exclusive: the start of the day after the last day to include. */
  actionTimestampTo?: string;
}

export interface GetPayinsParams {
  params: {
    nursing_home_id: string;
    page: number;
    pageSize: number;
    pending_charge_status: PayinStatusFilter[];
    /** Defaults to the charge date, newest first. */
    orderBy?: PayinSortColumn;
    orderDirection?: 'asc' | 'desc';
    filters?: PayinFilters;
  };
}

export interface ResidentWithBalance {
  id: string;
  firstname: string;
  lastname: string;
  balance: number;
  due_date: Date;
}

export interface ReceiptStatusCount {
  count: number;
  total: number;
}

export interface ReceiptTypeCount {
  count: number;
  total: number;
}

export interface GetDashboardResult {
  data: {
    receipts: Record<string, ReceiptStatusCount>;
    negative_balance: {
      count: number;
      residents: ResidentWithBalance[];
    };
    balance_below_threshold: {
      count: number;
      residents: ResidentWithBalance[];
    };
    receipt_types: Record<string, ReceiptTypeCount>;
    date_range: {
      from_date: Date;
      to_date: Date;
    };
  };
}

export interface GetDashboardParams {
  params: {
    nursing_home_id: string;
    month: string; // Format: "YYYY-MM"
  };
}

export interface CashListBalanceChange {
  cash_list_id: string;
  start_balance: number;
  end_balance: number;
  change: number;
}

export interface GetReportingOverviewResult {
  data: {
    from: string;
    to: string;
    cash_lists: CashListBalanceChange[];
    /** null if the legal entity has no Pliant organization */
    pliant: { change: number } | null;
  };
}

export interface GetReportingOverviewParams {
  params: {
    nursing_home_id: string;
    /** ISO datetime, inclusive */
    from: string;
    /** ISO datetime, exclusive: the start of the day after the last day to include. */
    to: string;
  };
}

export interface IReportingPhoenixApi extends Api {
  getFinance(dataIn: {
    params: {
      nursing_home_id: string;
    };
  }): Promise<FinanceResult>;
  getPayouts(params: GetPayoutsParams): Promise<GetPayoutsResult>;
  getPayoutReconciliation(
    params: GetPayoutReconciliationParams
  ): Promise<GetPayoutReconciliationResult>;
  getPayins(params: GetPayinsParams): Promise<GetPayinsResult>;
  getDashboard(params: GetDashboardParams): Promise<GetDashboardResult>;
  getOverview(
    params: GetReportingOverviewParams
  ): Promise<GetReportingOverviewResult>;
}

export class ReportingPhoenixApi implements IReportingPhoenixApi {
  baseUrl: string;
  headers: Headers;

  getFinance: IReportingPhoenixApi['getFinance'];
  getPayouts: IReportingPhoenixApi['getPayouts'];
  getPayoutReconciliation: IReportingPhoenixApi['getPayoutReconciliation'];
  getPayins: IReportingPhoenixApi['getPayins'];
  getDashboard: IReportingPhoenixApi['getDashboard'];
  getOverview: IReportingPhoenixApi['getOverview'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.headers = new Headers({
      'content-type': 'application/json',
      'x-pn-stripemode': 'live',
    });

    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getFinance = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/reporting/nursing-home/${params.nursing_home_id}/finance`,
    });

    this.getPayouts = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const query = new URLSearchParams();
        query.append('page', (params.page + 1).toString());
        query.append('page_size', params.pageSize.toString());

        // Deliberately NOT using `Filter` here: the backend merge helper
        // (merge_params_and_nursing_home_filter in reporting_controller.ex)
        // list-concats the decoded `filters` param, which only works when
        // Plug decodes it as a list — i.e. the index-less `filters[][...]`
        // form. `Filter`'s indexed `filters[0][...]` output decodes to a
        // map instead and crashes that merge.
        if (params.state) {
          query.append(`filters[][field]`, 'mangopay_state');
          query.append(`filters[][op]`, '==');
          query.append(`filters[][value]`, params.state);
        }
        if (params.orderBy) {
          query.append('order_by[]', params.orderBy);
          query.append('order_directions[]', params.orderDirection ?? 'desc');
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/payouts?${query.toString()}`;
      },
    });

    this.getPayoutReconciliation = get({
      authContext,
      headers: this.headers,
      url: (params) =>
        `${this.baseUrl}/reporting/nursing-home/${params.nursing_home_id}/payouts/${params.payout_id}/reconciliation`,
    });

    this.getPayins = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const filter = new Filter();

        // Bare `if` (no length check) is intentional: an explicit `[]`
        // still sends the (valueless) `in` filter, matching zero rows,
        // rather than being treated as absent like `fieldIfPresent` would.
        if (params.pending_charge_status) {
          filter.field(
            'pending_charge_status',
            'in',
            params.pending_charge_status
          );
        }
        filter.fieldIfPresent(
          'action_timestamp',
          '>=',
          params.filters?.actionTimestampFrom
        );
        filter.fieldIfPresent(
          'action_timestamp',
          '<',
          params.filters?.actionTimestampTo
        );

        const query = filter.build();
        query.append('page', (params.page + 1).toString());
        query.append('page_size', params.pageSize.toString());

        const orderBy = params.orderBy ?? 'charge_date';
        const orderDirection = params.orderDirection ?? 'desc';
        const tieBreakers =
          orderBy === 'charge_date' ? ['updated_at', 'id'] : ['id'];
        for (const column of [orderBy, ...tieBreakers]) {
          query.append('order_by[]', column);
          query.append('order_directions[]', orderDirection);
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/payins?${query.toString()}`;
      },
    });

    this.getDashboard = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const query = new URLSearchParams();

        if (params.month) {
          query.append('month', params.month);
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/dashboard${query.toString() ? `?${query.toString()}` : ''}`;
      },
    });

    this.getOverview = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams({ from: params.from, to: params.to });
        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/overview?${query.toString()}`;
      },
    });
  }
}
